import * as z from 'zod'

import { getProductFlowConfiguration } from '@flows/configs/shared/flowConfiguration'
import type { FlowConfigProps } from '@flows/configs/types'
import type { AnySteps } from '@flows/core/types/stepTypes'
import { SubFlow } from '@flows/insurance/types/subFlowTypes'
import * as flowConfigUtils from '@flows/insurance/utils'

import contents_bike from './steps/contents_bike'
import contents_bike_insured_sum from './steps/contents_bike_insured_sum'
import contents_birthday from './steps/contents_birthday'
import contents_damages from './steps/contents_damages'
import contents_deductibles from './steps/contents_deductibles'
import contents_glass from './steps/contents_glass'
import contents_insurance_cancelled from './steps/contents_insurance_cancelled'
import contents_insurance_owned from './steps/contents_insurance_owned'
import contents_location from './steps/contents_location'
import contents_natural_disasters from './steps/contents_natural_disasters'
import living_space from './steps/living_space'
import permanent_residence from './steps/permanent_residence'
import wg_insured from './steps/wg_insured'
import wg_members from './steps/wg_members'
import type { ContentsNeoStepConfigParams } from './types'

const productKeys = [
  'contents_premium_basic_neodigital_de',
  'contents_comfort_basic_neodigital_de',
] as const

const getContentsConfig = ({
  clientHelpers,
  subFlow = SubFlow.Contents,
  productKey = 'contents_premium_basic_neodigital_de',
}: FlowConfigProps) => {
  const parsedProductKey = z.enum(productKeys).safeParse(productKey)

  if (!parsedProductKey.success) {
    throw new Error(
      `Assertion: Invalid product key passed to contents neo flow, expected one of "${productKeys}" but got ${productKey}`
    )
  }
  const contentsProductKey = parsedProductKey.data
  const helperFunctions = flowConfigUtils.generateGetFlowValueFunctions(contentsProductKey)
  const stepParams: ContentsNeoStepConfigParams = {
    productKey: contentsProductKey,
    clientHelpers,
    subFlow,
    helperFunctions,
  }
  const steps = {
    contents_birthday: contents_birthday(stepParams),
    living_space: living_space(stepParams),
    permanent_residence: permanent_residence(stepParams),
    contents_insurance_owned: contents_insurance_owned(stepParams),
    contents_damages: contents_damages(stepParams),
    contents_insurance_cancelled: contents_insurance_cancelled(stepParams),
    contents_deductibles: contents_deductibles(stepParams),
    contents_glass: contents_glass(stepParams),
    contents_location: contents_location(stepParams),
    contents_bike: contents_bike(stepParams),
    contents_bike_insured_sum: contents_bike_insured_sum(stepParams),
    wg_insured: wg_insured(stepParams),
    wg_members: wg_members(stepParams),
    contents_natural_disasters: contents_natural_disasters(stepParams),
  } as const satisfies AnySteps

  return getProductFlowConfiguration(contentsProductKey, steps, {
    constants: {
      FIRST_STEP_NAME: 'contents_birthday',
    },
  })
}

export default getContentsConfig
